/**
 * GULLYO cart flight — wings mark flies to the header cart on successful add-to-bag.
 */
(function () {
  const EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
  const CART_HIT_CLASS = 'gullyo-cart-hit';
  const BADGE_HIT_CLASS = 'gullyo-cart-badge-hit';
  const DESKTOP_DURATION = 1750;
  const MOBILE_DURATION = 1500;
  const APPEAR_MS = 220;
  const HOLD_MS = 140;
  const CART_HIT_MS = 220;
  const MAX_CONCURRENT = 3;
  const GOLD_GLOW =
    'brightness(1.14) saturate(1.45) drop-shadow(0 0 14px rgba(242, 194, 20, 0.62)) drop-shadow(0 2px 10px rgba(17, 17, 17, 0.14))';
  const GOLD_GLOW_SOFT =
    'brightness(1.1) saturate(1.3) drop-shadow(0 0 8px rgba(242, 194, 20, 0.4)) drop-shadow(0 2px 8px rgba(17, 17, 17, 0.1))';

  /** @type {{ clone: HTMLElement, animation: Animation | null, cleanup: () => void }[]} */
  let activeFlights = [];

  /** @type {{ el: Element, time: number } | null} */
  let cartTargetCache = null;

  /** @type {number} */
  let inflightCount = 0;

  /** @type {Array<() => void>} */
  let idleWaiters = [];

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function isMobileViewport() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  function getFlightDuration() {
    return isMobileViewport() ? MOBILE_DURATION : DESKTOP_DURATION;
  }

  function getWingsSize() {
    return isMobileViewport() ? 38 : 48;
  }

  function getAppearOffset() {
    return APPEAR_MS / getFlightDuration();
  }

  function getHoldOffset() {
    return (APPEAR_MS + HOLD_MS) / getFlightDuration();
  }

  function markFlightStart() {
    inflightCount += 1;
  }

  function markFlightEnd() {
    inflightCount = Math.max(0, inflightCount - 1);
    if (inflightCount === 0) {
      const waiters = idleWaiters.splice(0);
      waiters.forEach((resolve) => resolve());
    }
  }

  function waitForFlightIdle() {
    if (inflightCount === 0 || prefersReducedMotion()) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      idleWaiters.push(resolve);
    });
  }

  /**
   * @returns {string}
   */
  function getWingsMarkUrl() {
    if (typeof window.GULLYO_WINGS_MARK_URL === 'string' && window.GULLYO_WINGS_MARK_URL) {
      return window.GULLYO_WINGS_MARK_URL;
    }

    const datasetUrl = document.documentElement.dataset.gullyoWingsMarkUrl;
    if (datasetUrl) return datasetUrl;

    const iconLink = document.querySelector('link[rel="icon"][sizes="32x32"]');
    return iconLink?.href || '';
  }

  /**
   * @returns {Element | null}
   */
  function getVisibleCartTarget() {
    const now = Date.now();
    if (cartTargetCache && now - cartTargetCache.time < 400) {
      const rect = cartTargetCache.el.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) return cartTargetCache.el;
    }

    const selectors = ['[data-gullyo-cart-target]', 'cart-icon.header-actions__cart-icon', '.header-actions__cart-icon'];

    for (const selector of selectors) {
      const nodes = document.querySelectorAll(selector);
      for (const el of nodes) {
        const rect = el.getBoundingClientRect();
        const style = getComputedStyle(el);
        if (
          rect.width > 0 &&
          rect.height > 0 &&
          style.display !== 'none' &&
          style.visibility !== 'hidden' &&
          Number(style.opacity) > 0
        ) {
          cartTargetCache = { el, time: now };
          return el;
        }
      }
    }

    return null;
  }

  /**
   * @param {Element | null | undefined} triggerEl
   * @returns {{ left: number, top: number, width: number, height: number, centerX: number, centerY: number }}
   */
  function getOriginBox(triggerEl) {
    const size = getWingsSize();
    const anchor =
      (triggerEl instanceof Element && triggerEl) ||
      (document.activeElement instanceof Element ? document.activeElement : null) ||
      document.body;
    const rect = anchor.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    return {
      left: centerX - size / 2,
      top: centerY - size / 2,
      width: size,
      height: size,
      centerX,
      centerY,
    };
  }

  /**
   * @param {number} endX
   * @param {number} endY
   */
  function computeArcMidpoint(endX, endY) {
    const mobile = isMobileViewport();
    const horizontal = mobile ? 0.48 : 0.54;
    const midX = endX * horizontal;
    const arcLift = mobile ? 18 : 28;
    const midY = Math.min(endY * 0.34 - arcLift, -20);
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 800;
    const maxUpward = -(viewportHeight * 0.14);

    return {
      midX,
      midY: Math.max(midY, maxUpward),
    };
  }

  function trimActiveFlights() {
    while (activeFlights.length >= MAX_CONCURRENT) {
      const oldest = activeFlights.shift();
      oldest?.cleanup?.();
    }
  }

  /**
   * @param {HTMLElement} clone
   * @param {Animation | null} animation
   */
  function registerFlight(clone, animation) {
    trimActiveFlights();

    const flight = {
      clone,
      animation,
      cleanup() {
        if (animation) {
          try {
            animation.cancel();
          } catch (_) {
            /* no-op */
          }
        }
        clone.remove();
        activeFlights = activeFlights.filter((item) => item !== flight);
      },
    };

    activeFlights.push(flight);
    return flight;
  }

  /**
   * @param {{ left: number, top: number, width: number, height: number, centerX: number, centerY: number }} start
   * @param {Element} cartTarget
   * @param {() => void} [onComplete]
   */
  function animateWingsToCart(start, cartTarget, onComplete) {
    const wingsUrl = getWingsMarkUrl();
    if (!wingsUrl) {
      onComplete?.();
      return;
    }

    const clone = document.createElement('div');
    clone.className = 'gullyo-fly-clone gullyo-fly-clone--wings';
    clone.setAttribute('aria-hidden', 'true');

    const trail = document.createElement('div');
    trail.className = 'gullyo-fly-clone__trail';
    trail.setAttribute('aria-hidden', 'true');

    const img = document.createElement('img');
    img.alt = '';
    img.decoding = 'async';
    img.draggable = false;
    img.src = wingsUrl;

    clone.appendChild(trail);
    clone.appendChild(img);

    clone.style.width = `${start.width}px`;
    clone.style.height = `${start.height}px`;
    clone.style.left = `${start.left}px`;
    clone.style.top = `${start.top}px`;

    document.body.appendChild(clone);

    const destRect = cartTarget.getBoundingClientRect();
    const destX = destRect.left + destRect.width / 2;
    const destY = destRect.top + destRect.height / 2;
    const endX = destX - start.centerX;
    const endY = destY - start.centerY;
    const { midX, midY } = computeArcMidpoint(endX, endY);
    const duration = getFlightDuration();
    const appear = getAppearOffset();
    const hold = getHoldOffset();

    const motion = clone.animate(
      [
        {
          transform: 'translate3d(0, 10px, 0) scale(0.85) rotate(0deg)',
          opacity: 0,
        },
        {
          offset: appear,
          transform: 'translate3d(0, -16px, 0) scale(1.08) rotate(-7deg)',
          opacity: 1,
        },
        {
          offset: hold,
          transform: 'translate3d(0, -16px, 0) scale(1.08) rotate(-6deg)',
          opacity: 1,
        },
        {
          offset: 0.48,
          transform: `translate3d(${midX}px, ${midY}px, 0) scale(1) rotate(-3deg)`,
          opacity: 1,
        },
        {
          offset: 0.78,
          transform: `translate3d(${endX * 0.86}px, ${endY * 0.86}px, 0) scale(0.92) rotate(-1deg)`,
          opacity: 1,
        },
        {
          offset: 0.9,
          transform: `translate3d(${endX}px, ${endY}px, 0) scale(0.86) rotate(0deg)`,
          opacity: 1,
        },
        {
          offset: 0.96,
          transform: `translate3d(${endX}px, ${endY}px, 0) scale(0.8) rotate(0deg)`,
          opacity: 0.55,
        },
        {
          transform: `translate3d(${endX}px, ${endY}px, 0) scale(0.76) rotate(0deg)`,
          opacity: 0,
        },
      ],
      {
        duration,
        easing: EASE,
        fill: 'forwards',
      }
    );

    img.animate(
      [
        { filter: 'brightness(1) saturate(1) drop-shadow(0 0 0 rgba(242, 194, 20, 0))' },
        { offset: appear, filter: GOLD_GLOW },
        { offset: hold, filter: GOLD_GLOW },
        { offset: 0.5, filter: GOLD_GLOW_SOFT },
        { offset: 0.88, filter: GOLD_GLOW_SOFT },
        { filter: 'brightness(1) saturate(1) drop-shadow(0 0 0 rgba(242, 194, 20, 0))' },
      ],
      { duration, easing: EASE, fill: 'forwards' }
    );

    trail.animate(
      [
        { opacity: 0, transform: 'scale(0.55)' },
        { offset: appear, opacity: 0.92, transform: 'scale(1.05)' },
        { offset: hold, opacity: 0.78, transform: 'scale(1)' },
        { offset: 0.5, opacity: 0.52, transform: 'scale(0.9)' },
        { offset: 0.82, opacity: 0.28, transform: 'scale(0.72)' },
        { offset: 0.94, opacity: 0.12, transform: 'scale(0.55)' },
        { opacity: 0, transform: 'scale(0.4)' },
      ],
      { duration, easing: EASE, fill: 'forwards' }
    );

    const flight = registerFlight(clone, motion);

    motion.finished
      .then(() => {
        flight.cleanup();
        onComplete?.();
      })
      .catch(() => {
        flight.cleanup();
        onComplete?.();
      });
  }

  /**
   * @param {Element | null | undefined} cartTarget
   */
  function pulseCartIcon(cartTarget) {
    if (!cartTarget || !(cartTarget instanceof Element)) return;

    cartTarget.classList.remove(CART_HIT_CLASS);
    void cartTarget.getBoundingClientRect();
    cartTarget.classList.add(CART_HIT_CLASS);

    const badge =
      cartTarget.querySelector(
        '.cart-bubble, .cart-bubble__text-count, .header-actions__cart-count, [data-cart-count]'
      ) || document.querySelector('.cart-bubble, .header-actions__cart-count, [data-cart-count]');

    if (badge instanceof Element) {
      badge.classList.remove(BADGE_HIT_CLASS);
      void badge.getBoundingClientRect();
      badge.classList.add(BADGE_HIT_CLASS);
      window.setTimeout(() => {
        badge.classList.remove(BADGE_HIT_CLASS);
      }, CART_HIT_MS);
    }

    window.setTimeout(() => {
      cartTarget.classList.remove(CART_HIT_CLASS);
    }, CART_HIT_MS);
  }

  /**
   * @param {{ triggerEl?: Element | null, productImageEl?: Element | null, button?: Element | null, form?: Element | null, root?: Element | null }} options
   */
  function playCartFlightAnimation(options = {}) {
    const triggerEl = options.triggerEl || options.button;
    const cartTarget = getVisibleCartTarget();

    if (!cartTarget || !triggerEl) {
      return;
    }

    markFlightStart();

    if (prefersReducedMotion()) {
      pulseCartIcon(cartTarget);
      markFlightEnd();
      return;
    }

    const start = getOriginBox(triggerEl);
    const duration = getFlightDuration();

    window.setTimeout(() => {
      pulseCartIcon(cartTarget);
    }, Math.max(0, duration - CART_HIT_MS));

    animateWingsToCart(start, cartTarget, () => {
      markFlightEnd();
    });
  }

  /**
   * Deferred flight — call finishOnSuccess() only after cart add succeeds.
   * @param {{ button?: Element | null, form?: Element | null, root?: Element | null, triggerEl?: Element | null }} context
   */
  function begin(context = {}) {
    const state = {
      cancelled: false,
      context,
    };

    return {
      cancel() {
        state.cancelled = true;
      },
      finishOnSuccess() {
        if (state.cancelled) return;

        playCartFlightAnimation({
          triggerEl: state.context.button || state.context.triggerEl,
          form: state.context.form,
          root: state.context.root,
        });
      },
      animationPromise: Promise.resolve(),
    };
  }

  function cleanupFlyingClone() {
    activeFlights.forEach((flight) => flight.cleanup());
    activeFlights = [];
  }

  window.playCartFlightAnimation = playCartFlightAnimation;
  window.playGullyoCartFlightAnimation = playCartFlightAnimation;
  window.GullyoFlyToCart = {
    begin,
    playCartFlightAnimation,
    cleanupFlyingClone,
    getVisibleCartTarget,
    prefersReducedMotion,
    pulseCartIcon,
    waitForFlightIdle,
    isFlightActive: () => inflightCount > 0,
  };
})();
